import type { TroopMovement } from '@/types';

// ---------------------------------------------------------------------------
// OSINT-based troop movement scenarios
// Bidirectional arrows between warzone countries
// ---------------------------------------------------------------------------

export const TROOP_MOVEMENTS: TroopMovement[] = [
  // Russia ↔ Ukraine
  {
    id: 'tm-ru-ua-main',
    fromCountry: 'Russia',
    fromLon: 37.6,
    fromLat: 55.7,
    toCountry: 'Ukraine (Frontline)',
    toLon: 36.5,
    toLat: 48.0,
    type: 'ground',
    scale: 'corps',
    description: 'Russian ground forces advance along multiple fronts in eastern Ukraine',
    severity: 'critical',
    startDate: '2022-02-24',
    relatedNews: [
      { title: 'Russia intensifies offensive in Zaporizhzhia region with armored columns', source: 'Institute for Study of War', publishedAt: '2026-03-06T18:00:00Z' },
      { title: 'Ukrainian forces report increased Russian pressure near Avdiivka', source: 'Reuters', publishedAt: '2026-03-06T12:00:00Z' },
      { title: 'ISW: Russia deploying additional BTGs from eastern military district', source: 'Kyiv Independent', publishedAt: '2026-03-05T20:00:00Z' },
    ],
  },
  {
    id: 'tm-ua-ru-counter',
    fromCountry: 'Ukraine',
    fromLon: 30.5,
    fromLat: 50.4,
    toCountry: 'Russia (Kursk)',
    toLon: 36.2,
    toLat: 51.7,
    type: 'ground',
    scale: 'brigade',
    description: 'Ukrainian forces maintain cross-border incursion in Kursk Oblast',
    severity: 'high',
    startDate: '2024-08-06',
    relatedNews: [
      { title: 'Ukraine continues holding territory in Kursk despite Russian counteroffensive', source: 'BBC', publishedAt: '2026-03-06T10:00:00Z' },
      { title: 'Ukraine uses Kursk incursion as bargaining chip in ceasefire talks', source: 'FT', publishedAt: '2026-03-05T14:00:00Z' },
    ],
  },
  // Israel ↔ Gaza/Lebanon
  {
    id: 'tm-il-gz',
    fromCountry: 'Israel',
    fromLon: 34.8,
    fromLat: 31.8,
    toCountry: 'Gaza Strip',
    toLon: 34.4,
    toLat: 31.3,
    type: 'ground',
    scale: 'division',
    description: 'IDF ground operations continue in Gaza Strip',
    severity: 'critical',
    startDate: '2023-10-07',
    relatedNews: [
      { title: 'IDF resumes ground operations in Gaza after ceasefire collapse', source: 'Haaretz', publishedAt: '2026-03-07T06:00:00Z' },
      { title: 'Israeli tanks enter Rafah crossing area as offensive widens', source: 'Al Jazeera', publishedAt: '2026-03-06T22:00:00Z' },
      { title: 'Gaza civilian death toll reaches 50,000 as war enters 17th month', source: 'UN OCHA', publishedAt: '2026-03-06T12:00:00Z' },
    ],
  },
  {
    id: 'tm-il-lb',
    fromCountry: 'Israel',
    fromLon: 35.2,
    fromLat: 32.0,
    toCountry: 'Lebanon (Hezbollah)',
    toLon: 35.5,
    toLat: 33.8,
    type: 'air',
    scale: 'brigade',
    description: 'Israeli airstrikes target Hezbollah positions in southern Lebanon',
    severity: 'high',
    startDate: '2023-10-08',
    relatedNews: [
      { title: 'IDF strikes Hezbollah command infrastructure in Beirut southern suburbs', source: 'Times of Israel', publishedAt: '2026-03-07T04:00:00Z' },
    ],
  },
  // China ↔ Taiwan Strait
  {
    id: 'tm-cn-tw',
    fromCountry: 'China (PLA)',
    fromLon: 119.3,
    fromLat: 26.0,
    toCountry: 'Taiwan Strait',
    toLon: 121.0,
    toLat: 24.5,
    type: 'naval',
    scale: 'corps',
    description: 'PLA Eastern Theater exercises with carrier group in Taiwan Strait',
    severity: 'high',
    startDate: '2026-02-01',
    relatedNews: [
      { title: 'China conducts largest Taiwan Strait exercise since 2022', source: 'South China Morning Post', publishedAt: '2026-03-07T08:00:00Z' },
      { title: 'PLA deploys 3 carrier groups for joint exercises near Taiwan', source: 'Defense News', publishedAt: '2026-03-06T16:00:00Z' },
      { title: 'Taiwan scrambles 102 sorties as PLA aircraft enter ADIZ', source: 'CNA', publishedAt: '2026-03-06T12:00:00Z' },
    ],
  },
  {
    id: 'tm-us-tw',
    fromCountry: 'USA (7th Fleet)',
    fromLon: 135.0,
    fromLat: 26.0,
    toCountry: 'Taiwan Strait',
    toLon: 121.5,
    toLat: 24.5,
    type: 'naval',
    scale: 'brigade',
    description: 'USS Ronald Reagan CSG transits Taiwan Strait in freedom of navigation operation',
    severity: 'medium',
    startDate: '2026-03-05',
    relatedNews: [
      { title: 'US carrier strike group transits Taiwan Strait amid China exercises', source: 'USNI News', publishedAt: '2026-03-07T02:00:00Z' },
    ],
  },
  // US ↔ Middle East
  {
    id: 'tm-us-me',
    fromCountry: 'USA',
    fromLon: -80.0,
    fromLat: 32.0,
    toCountry: 'Middle East (CENTCOM)',
    toLon: 51.5,
    toLat: 25.3,
    type: 'air',
    scale: 'division',
    description: 'USAF B-52 deployments to Diego Garcia and Gulf as Iran deterrence package',
    severity: 'high',
    startDate: '2026-02-20',
    relatedNews: [
      { title: 'US deploys additional B-52 bombers to Diego Garcia amid Iran tensions', source: 'Defense News', publishedAt: '2026-03-06T20:00:00Z' },
      { title: 'CENTCOM activates Jordan-based forces as Iran-Israel conflict escalates', source: 'AP', publishedAt: '2026-03-06T18:00:00Z' },
    ],
  },
  // North Korea → Russia (arms logistics)
  {
    id: 'tm-kp-ru',
    fromCountry: 'North Korea',
    fromLon: 127.0,
    fromLat: 39.0,
    toCountry: 'Russia',
    toLon: 131.0,
    toLat: 43.0,
    type: 'logistics',
    scale: 'corps',
    description: 'DPRK arms shipments and troops deployed to support Russian war effort',
    severity: 'high',
    startDate: '2024-10-01',
    relatedNews: [
      { title: 'South Korea confirms 12,000 DPRK troops now fighting in Russia', source: 'Yonhap', publishedAt: '2026-03-06T08:00:00Z' },
      { title: 'North Korea ships 2 million artillery shells to Russia via rail', source: 'Bloomberg', publishedAt: '2026-03-05T14:00:00Z' },
    ],
  },
];
